/**
 * Base Zod schemas for common field types with i18n support
 * These schemas provide both static and internationalized validation
 */

import { z } from 'zod';
import { validateEmail } from '../utils/regexes/email';
import { validatePhoneNumber, validatePhoneNumberStrict } from '../utils/regexes/phone-number';
import { validatePassword } from '../utils/regexes/password';
import { validatePostalCode, validatePostalCodeStrict } from '../utils/regexes/postal-code';
import { ITranslationContext } from '../utils/resources/types';
import { getTranslatedMessage } from '../utils/resources/resource-loader';

function getMessage(key: string, fallback: string, context?: ITranslationContext): string {
    if (!context) {
        return fallback;
    }

    let message = getTranslatedMessage(key, context, '');

    if (!message) {
        const fallbackKeys = ['fieldIncorrectErrorText'];
        for (const fallbackKey of fallbackKeys) {
            message = getTranslatedMessage(fallbackKey, context, '');
            if (message) {
                break;
            }
        }
    }

    return message || fallback;
}

export function createBaseFieldSchemas(translationContext?: ITranslationContext) {
    const textSchema = z
        .string()
        .min(1, getMessage('fieldIncorrectErrorText', 'This field is required', translationContext))
        .trim();

    const optionalTextSchema = textSchema.optional();

    const textWithLengthSchema = (min: number, max?: number) =>
        textSchema
            .min(min, getMessage('fieldIncorrectErrorText', `Must be at least ${min} characters`, translationContext))
            .max(max || 255, getMessage('fieldIncorrectErrorText', `Cannot exceed ${max || 255} characters`, translationContext));

    const nameSchema = textSchema
        .min(1, getMessage('addressNameRequiredErrorMessage', 'Name is required', translationContext))
        .max(50, getMessage('nameValidationErrorMax', 'Name cannot exceed 50 characters', translationContext))
        .regex(
            /^[a-zA-ZÀ-ÿ\s-']+$/,
            getMessage('nameValidationErrorFormat', 'Name can only contain letters, spaces, hyphens, and apostrophes', translationContext)
        );

    const optionalNameSchema = nameSchema.optional();

    const emailSchema = z
        .string()
        .min(1, getMessage('emailErrortext', 'Email is required', translationContext))
        .refine(
            email => validateEmail(email).isValid,
            email => ({
                message: getMessage(
                    'invalidEmailAddressError',
                    validateEmail(email).errors[0] || 'Please enter a valid email address',
                    translationContext
                )
            })
        );

    const optionalEmailSchema = emailSchema.optional();

    const phoneSchema = z
        .string()
        .min(1, getMessage('phoneValidationErrorRequired', 'Phone number is required', translationContext))
        .min(6, getMessage('phoneValidationErrorMin', 'Phone number must be at least 6 characters', translationContext))
        .max(15, getMessage('phoneValidationErrorMax', 'Phone number must be at most 15 characters', translationContext))
        .refine(
            phone => validatePhoneNumber(phone).isValid,
            phone => ({
                message: getMessage(
                    'phoneValidationErrorMessage',
                    validatePhoneNumber(phone).errors[0] || 'Please enter a valid phone number',
                    translationContext
                )
            })
        );

    const optionalPhoneSchema = phoneSchema.optional();

    /**
     * Phone number with country code validation
     * Note: Currently uses simplified validation (ignores country code)
     * For strict country-specific validation, use validatePhoneNumberStrict
     */
    const phoneWithCountrySchema = (countryCode?: string) =>
        z
            .string()
            .min(6, getMessage('phoneValidationErrorMin', 'Phone number must be at least 6 characters', translationContext))
            .max(15, getMessage('phoneValidationErrorMax', 'Phone number must be at most 15 characters', translationContext))
            .refine(
                phone => validatePhoneNumber(phone).isValid,
                phone => ({
                    message: getMessage(
                        'fieldIncorrectErrorText',
                        validatePhoneNumber(phone).errors[0] || 'Please enter a valid phone number',
                        translationContext
                    )
                })
            );

    /**
     * Phone number with strict country code validation (versioned for future use)
     * Uses validatePhoneNumberStrict with country-specific patterns
     */
    const phoneWithCountrySchemaStrict = (countryCode?: string) =>
        z
            .string()
            .min(1, getMessage('fieldIncorrectErrorText', 'Phone number is required', translationContext))
            .refine(
                phone => validatePhoneNumberStrict(phone, countryCode).isValid,
                phone => ({
                    message: getMessage(
                        'fieldIncorrectErrorText',
                        validatePhoneNumberStrict(phone, countryCode).errors[0] || 'Please enter a valid phone number',
                        translationContext
                    )
                })
            );

    const passwordSchema = z
        .string()
        .min(1, getMessage('invalidPasswordError', 'Password is required', translationContext))
        .refine(
            password => validatePassword(password).isValid,
            password => ({
                message: getMessage(
                    'invalidPasswordError',
                    validatePassword(password).errors[0] || 'Please enter a valid password',
                    translationContext
                )
            })
        );

    const postalCodeSchema = z
        .string()
        .min(1, getMessage('addressZipCodeRequiredErrorMessage', 'Postal code is required', translationContext))
        .refine(
            code => validatePostalCode(code).isValid,
            code => ({
                message: getMessage(
                    'fieldIncorrectErrorText',
                    validatePostalCode(code).errors[0] || 'Please enter a valid postal code',
                    translationContext
                )
            })
        );

    const postalCodeWithCountrySchema = (countryCode?: string) =>
        z
            .string()
            .min(1, getMessage('addressZipCodeRequiredErrorMessage', 'Postal code is required', translationContext))
            .refine(
                code => validatePostalCode(code, countryCode).isValid,
                code => ({
                    message: getMessage(
                        'fieldIncorrectErrorText',
                        validatePostalCode(code, countryCode).errors[0] || 'Please enter a valid postal code',
                        translationContext
                    )
                })
            );

    /**
     * Postal code with strict country code validation (versioned for future use)
     * Uses validatePostalCodeStrict with country-specific patterns
     */
    const postalCodeWithCountrySchemaStrict = (countryCode?: string) =>
        z
            .string()
            .min(1, getMessage('addressZipCodeRequiredErrorMessage', 'Postal code is required', translationContext))
            .refine(
                code => validatePostalCodeStrict(code, countryCode).isValid,
                code => ({
                    message: getMessage(
                        'fieldIncorrectErrorText',
                        validatePostalCodeStrict(code, countryCode).errors[0] || 'Please enter a valid postal code',
                        translationContext
                    )
                })
            );

    const dateSchema = z
        .string()
        .min(1, getMessage('fieldIncorrectErrorText', 'Date is required', translationContext))
        .refine(date => !isNaN(Date.parse(date)), getMessage('fieldIncorrectErrorText', 'Please enter a valid date', translationContext));

    const optionalDateSchema = dateSchema.optional();

    const birthdaySchema = dateSchema.refine(date => {
        const birthDate = new Date(date);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        return age >= 13;
    }, getMessage('fieldIncorrectErrorText', 'You must be at least 13 years old', translationContext));

    const optionalBirthdaySchema = birthdaySchema.optional();

    const countryCodeSchema = z
        .string()
        .min(
            2,
            getMessage(
                'addressThreeLetterISORegionNameRequiredErrorMessage',
                'Country code must be at least 2 characters',
                translationContext
            )
        )
        .max(3, getMessage('fieldIncorrectErrorText', 'Country code cannot exceed 3 characters', translationContext))
        .regex(/^[A-Z]{2,3}$/, getMessage('fieldIncorrectErrorText', 'Country code must be uppercase letters only', translationContext));

    const optionalCountryCodeSchema = countryCodeSchema.optional();

    const checkboxSchema = z.boolean();

    const requiredCheckboxSchema = z
        .boolean()
        .refine(value => value === true, getMessage('fieldIncorrectErrorText', 'This field must be checked', translationContext));

    const optionalCheckboxSchema = checkboxSchema.optional().default(false);

    const addressSchemas = {
        name: z
            .string()
            .min(1, getMessage('addressNameRequiredErrorMessage', 'Name is required', translationContext))
            .max(100, getMessage('fieldIncorrectErrorText', 'Name is too long', translationContext)),

        street: z
            .string()
            .min(1, getMessage('addressStreetRequiredErrorMessage', 'Street address is required', translationContext))
            .max(200, getMessage('fieldIncorrectErrorText', 'Address is too long', translationContext)),

        city: z
            .string()
            .min(1, getMessage('addressCityRequiredErrorMessage', 'City is required', translationContext))
            .max(100, getMessage('fieldIncorrectErrorText', 'City name is too long', translationContext)),

        state: z
            .string()
            .min(1, getMessage('addressStateRequiredErrorMessage', 'State is required', translationContext))
            .max(100, getMessage('fieldIncorrectErrorText', 'State name is too long', translationContext)),

        county: z
            .string()
            .min(1, getMessage('addressCountyRequiredErrorMessage', 'County is required', translationContext))
            .max(100, getMessage('fieldIncorrectErrorText', 'County name is too long', translationContext)),

        country: z
            .string()
            .min(1, getMessage('addressThreeLetterISORegionNameRequiredErrorMessage', 'Country is required', translationContext))
            .length(3, getMessage('fieldIncorrectErrorText', 'Invalid country code', translationContext)),

        zipCode: postalCodeSchema,
        zipCodeWithCountry: postalCodeWithCountrySchema
    };

    return {
        textSchema,
        optionalTextSchema,
        textWithLengthSchema,
        nameSchema,
        optionalNameSchema,
        emailSchema,
        optionalEmailSchema,
        phoneSchema,
        optionalPhoneSchema,
        phoneWithCountrySchema,
        phoneWithCountrySchemaStrict,
        passwordSchema,
        postalCodeSchema,
        postalCodeWithCountrySchema,
        postalCodeWithCountrySchemaStrict,
        dateSchema,
        optionalDateSchema,
        birthdaySchema,
        optionalBirthdaySchema,
        countryCodeSchema,
        optionalCountryCodeSchema,
        checkboxSchema,
        requiredCheckboxSchema,
        optionalCheckboxSchema,
        addressSchemas
    };
}

export const {
    textSchema,
    optionalTextSchema,
    textWithLengthSchema,
    nameSchema,
    optionalNameSchema,
    emailSchema,
    optionalEmailSchema,
    phoneSchema,
    optionalPhoneSchema,
    phoneWithCountrySchema,
    phoneWithCountrySchemaStrict,
    passwordSchema,
    postalCodeSchema,
    postalCodeWithCountrySchema,
    postalCodeWithCountrySchemaStrict,
    dateSchema,
    optionalDateSchema,
    birthdaySchema,
    optionalBirthdaySchema,
    countryCodeSchema,
    optionalCountryCodeSchema,
    checkboxSchema,
    requiredCheckboxSchema,
    optionalCheckboxSchema
} = createBaseFieldSchemas();
